import React, { useState, useRef, useEffect } from 'react';
import './PersonalitySliderModal.css';

interface PersonalitySliderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { [key: string]: number }) => void;
  sliders: Array<{
    id: string;
    label: string;
    value: number;
    min: number;
    max: number;
  }>;
}

export const PersonalitySliderModal: React.FC<PersonalitySliderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  sliders
}) => {
  const [values, setValues] = useState<{ [key: string]: number }>({});
  const modalRef = useRef<HTMLDivElement>(null);

  // Initialize values when modal opens
  useEffect(() => {
    if (isOpen && sliders) {
      const initialValues: { [key: string]: number } = {};
      sliders.forEach(slider => {
        initialValues[slider.id] = slider.value;
      });
      setValues(initialValues);
    }
  }, [isOpen, sliders]);

  // Close modal on overlay click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSliderChange = (sliderId: string, value: number) => {
    setValues(prev => ({ ...prev, [sliderId]: value }));
  };

  const handleSubmit = () => {
    onSubmit(values);
    onClose();
  };

  const getSliderIcon = (sliderId: string): React.JSX.Element => {
    console.log('🎨 MODAL SLIDER ICON v4.0: Using new SVG assets for', sliderId);
    
    const iconStyle = {
      width: '20px',
      height: '20px',
      flexShrink: 0
    };

    switch (sliderId) {
      case 'energy':
        return (
          <img 
            src="/img/personality-energy.svg" 
            alt="Energy"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'data_depth':
        return (
          <img 
            src="/img/personality-data.svg" 
            alt="Data Depth"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'challenge':
        return (
          <img 
            src="/img/personality-challenge.svg" 
            alt="Challenge"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      case 'humor':
        return (
          <img 
            src="/img/personality-humor.svg" 
            alt="Humor"
            style={iconStyle}
            className="slider-icon-svg"
            data-version="4.1"
          />
        );
      default:
        return (
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            className="slider-icon-svg"
            data-version="4.0"
          >
            <circle cx="12" cy="12" r="3" stroke="#1F6C4F" fill="none" strokeWidth="2"/>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="#1F6C4F" strokeWidth="2"/>
          </svg>
        );
    }
  };

  const getSliderDescriptions = (sliderId: string) => {
    const descriptions: { [key: string]: { low: string; high: string } } = {
      energy: { low: 'Consistently Calm', high: 'Always Enthusiastic' },
      data_depth: { low: 'Simple & Clear', high: 'Detailed & Data-Rich' },
      challenge: { low: 'Gentle & Supportive', high: 'Direct & Challenging' },
      humor: { low: 'Professional', high: 'Playful & Fun' }
    };
    return descriptions[sliderId] || { low: 'Less', high: 'More' };
  };

  if (!isOpen) return null;

  return (
    <div className="personality-modal-overlay">
      <div className="personality-modal" ref={modalRef}>
        <div className="personality-modal-header">
          <h2 className="personality-modal-title">Customize My Coaching Style</h2>
          <p className="personality-modal-description">
            Help me understand how you prefer to receive coaching guidance
          </p>
          <button className="personality-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="personality-modal-content">
          <div className="sliders-container">
            {sliders?.map(slider => {
              const descriptions = getSliderDescriptions(slider.id);
              const currentValue = values[slider.id] || slider.value;
              const percentage = ((currentValue - slider.min) / (slider.max - slider.min)) * 100;

              return (
                <div key={slider.id} className="slider-item">
                  <div className="slider-header">
                    <div className="slider-label">
                      <div className="slider-icon">{getSliderIcon(slider.id)}</div>
                      <span className="slider-name">{slider.label}</span>
                    </div>
                    <div className="slider-value-badge">{currentValue}</div>
                  </div>

                  <div className="slider-descriptions">
                    <span className="slider-desc-low">{descriptions.low}</span>
                    <span className="slider-desc-high">{descriptions.high}</span>
                  </div>

                  <div 
                    className="slider-control"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left - 8; // Account for padding
                      const width = rect.width - 16; // Account for padding
                      const percentage = Math.max(0, Math.min(1, x / width));
                      const newValue = Math.round(slider.min + percentage * (slider.max - slider.min));
                      handleSliderChange(slider.id, newValue);
                    }}
                  >
                    <div className="slider-track-bg" />
                    <div 
                      className="slider-track-fill" 
                      style={{ width: `${percentage}%` }}
                    />
                    <div 
                      className="slider-thumb"
                      style={{ left: `${8 + (percentage / 100) * (100 - 16)}%` }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const startX = e.clientX;
                        const startValue = currentValue;
                        const controlRect = e.currentTarget.parentElement!.getBoundingClientRect();
                        const controlWidth = controlRect.width - 16;

                        const handleMouseMove = (e: MouseEvent) => {
                          const deltaX = e.clientX - startX;
                          const deltaValue = (deltaX / controlWidth) * (slider.max - slider.min);
                          const newValue = Math.max(slider.min, Math.min(slider.max, Math.round(startValue + deltaValue)));
                          handleSliderChange(slider.id, newValue);
                        };

                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                    />
                  </div>

                  <div className="slider-scale">
                    {Array.from({ length: slider.max - slider.min + 1 }, (_, i) => (
                      <span key={i} className="slider-scale-mark">
                        {slider.min + i}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="personality-modal-footer">
          <button className="personality-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="personality-modal-submit" onClick={handleSubmit}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}; 